import { KeyboardEvent, MouseEvent, ReactNode, useEffect, useLayoutEffect } from 'react'

import { useAstraPopover } from '../../AstraPopover/PopoverContext'
import { astraTwMerge } from '../../Utilities/astraTwMerge'
import { VirtualCell } from '../context/virtualGrid'
import { useDataGridContext } from '../hooks/useDataGridContext'
import { Cell, Id } from '../types'
import { toCellId } from '../utils'

export type ModalRender = (selectedCells: Cell[]) => ReactNode

type BaseCellProps<TRow extends { id: Id }, TCol extends { id: Id }> = {
    col: TCol
    row: TRow
    children: (row: TRow, col: TCol, isSelected: boolean) => ReactNode
    cellClassName?: (row: TRow, col: TCol) => string
    dataTest?: string
    isRowSelected?: boolean
    height?: number
    disableCellHover?: boolean
    /**
     * Optional action button to render in the top-right corner of the cell.
     * Will appear on hover/focus with the same behavior as the Cell component's edit button.
     */
    actionButton?: (row: TRow, col: TCol) => ReactNode
}

type Props<TRow extends { id: Id }, TCol extends { id: Id }> = BaseCellProps<TRow, TCol> & {
    modalContentRender?: ModalRender
}

const isCellSelected = (virtualCellRowIndex: number, virtualCellRolIndex: number, selectedVirtualCells: VirtualCell[]) =>
    selectedVirtualCells.some(cell => cell.r === virtualCellRowIndex && cell.c === virtualCellRolIndex)

const cursorStyles =
    "relative before:absolute before:bottom-0 before:right-0 before:z-priority before:h-px before:w-px before:rounded-full before:bg-brand-strong-rest before:content-[''] before:shadow-[0_0_0_5px_theme('backgroundColor.brand-strong-rest')]"

// Basic TableCell without modal support
const BasicTableCell = <TCol extends { id: Id }, TRow extends { id: Id }>({
    col,
    row,
    children,
    isRowSelected,
    cellClassName,
    height,
    disableCellHover,
    actionButton,
    dataTest,
}: BaseCellProps<TCol, TRow>) => {
    const { cursor, selectedCells, getVirtualCell, virtualSelectedCells, onKeyDown, onMouseUp, setDefault } = useDataGridContext()

    const cellId = toCellId({ rowId: row.id, colId: col.id })
    const isCursor = cursor?.rowId === String(row.id) && cursor.colId === String(col.id)
    const currentVirtualCell = getVirtualCell(row.id, col.id)

    const isThisCellSelected = !!selectedCells.find(cell => cell.rowId === String(row.id) && cell.colId === col.id)

    const shadowClasses = (() => {
        if (!currentVirtualCell || !isThisCellSelected) return ''
        const { r: rowIndex, c: colIndex } = currentVirtualCell
        return `shadow-borders${!isCellSelected(rowIndex - 1, colIndex, virtualSelectedCells) ? ' shadow-border-t' : ''}${!isCellSelected(rowIndex, colIndex + 1, virtualSelectedCells) ? ' shadow-border-r' : ''}${!isCellSelected(rowIndex + 1, colIndex, virtualSelectedCells) ? ' shadow-border-b' : ''}${!isCellSelected(rowIndex, colIndex - 1, virtualSelectedCells) ? ' shadow-border-l' : ''}`
    })()

    const handleClick = (e: MouseEvent) => {
        onMouseUp(e, row.id, col.id)
        e.preventDefault()
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Escape') {
            setDefault()
            return
        }
        onKeyDown(e)
    }

    return (
        <div
            id={cellId}
            autoFocus={isCursor}
            tabIndex={isCursor ? 0 : -1}
            data-test={dataTest}
            data-cell={`${row.id}_${col.id}`}
            data-row-id={row.id}
            data-col-id={col.id}
            style={{ height }}
            className={astraTwMerge(
                'group/cell relative h-full min-w-[28px] cursor-pointer self-stretch rounded-none border-b border-r bg-surface-base p-1 outline-none transition-all last:border-r-0',
                cellClassName?.(row, col),
                shadowClasses,
                isRowSelected && !isThisCellSelected ? '!bg-surface-strong' : '',
                !isThisCellSelected && !disableCellHover ? 'hover:bg-surface-muted' : '',
                isCursor ? [cursorStyles] : ''
            )}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
        >
            {children(row, col, isThisCellSelected)}
            {actionButton && (
                <div
                    className="absolute right-1 top-1 opacity-0 shadow transition-opacity focus-within:opacity-100 hover:opacity-100 focus:opacity-100 group-hover/cell:opacity-100"
                    onClick={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                >
                    {actionButton(row, col)}
                </div>
            )}
        </div>
    )
}

// TableCell with modal support
const ModalTableCell = <TCol extends { id: Id }, TRow extends { id: Id }>({
    col,
    row,
    children,
    modalContentRender,
    isRowSelected,
    cellClassName,
    height,
    dataTest,
    disableCellHover,
    actionButton,
}: Required<Pick<Props<TCol, TRow>, 'modalContentRender'>> & BaseCellProps<TCol, TRow>) => {
    const { cursor, selectedCells, getVirtualCell, virtualSelectedCells, onKeyDown, onMouseUp, setDefault, blockKeyInput, setBlockKeyInput } =
        useDataGridContext()

    const { openPopover, closePopover, isOpen } = useAstraPopover()

    // We want to signal to the context that the modal is open,
    // so that it can block other interactions outside the modal
    useEffect(() => {
        setBlockKeyInput(isOpen)
    }, [isOpen, setBlockKeyInput])

    const cellId = toCellId({ rowId: row.id, colId: col.id })
    const isCursor = cursor?.rowId === String(row.id) && cursor.colId === String(col.id)
    const currentVirtualCell = getVirtualCell(row.id, col.id)

    const isThisCellSelected = !!selectedCells.find(cell => cell.rowId === String(row.id) && cell.colId === col.id)

    const shadowClasses = (() => {
        if (!currentVirtualCell || !isThisCellSelected) return ''
        const { r: rowIndex, c: colIndex } = currentVirtualCell
        return `shadow-borders ${!isCellSelected(rowIndex - 1, colIndex, virtualSelectedCells) ? ' shadow-border-t' : ''}${!isCellSelected(rowIndex, colIndex + 1, virtualSelectedCells) ? ' shadow-border-r' : ''}${!isCellSelected(rowIndex + 1, colIndex, virtualSelectedCells) ? ' shadow-border-b' : ''}${!isCellSelected(rowIndex, colIndex - 1, virtualSelectedCells) ? ' shadow-border-l' : ''}`
    })()

    const handleClick = (e: MouseEvent<HTMLDivElement>) => {
        if (isThisCellSelected && selectedCells.length !== 0) {
            if (e.defaultPrevented) return
            openPopover(e.currentTarget, modalContentRender(selectedCells))
            return
        }

        onMouseUp(e, row.id, col.id)
        e.preventDefault() // prevents the trigger on the AstraPopover from being called
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (blockKeyInput) return
        if (e.key === 'Enter' && selectedCells.length !== 0) {
            openPopover(e.currentTarget, modalContentRender(selectedCells))
        }
        if (e.key === 'Escape') {
            setDefault()
            return
        }
        onKeyDown(e)
    }

    /**
     * Forcing the modal closing when there are no selected cells.
     * For cases when user on slow computer clicks fast on the cell, modal renders but user manages to click twice to deselect the cell
     * So modal opens without any selected cell.
     *
     *
     * Using `useLayoutEffect` here to close modal before the browser paint event to avoid flickering.
     * */
    useLayoutEffect(() => {
        if (selectedCells.length === 0) {
            closePopover()
        }
    }, [closePopover, selectedCells])

    return (
        <div
            id={cellId}
            autoFocus={isCursor}
            tabIndex={isCursor ? 0 : -1}
            data-cell={`${row.id}_${col.id}`}
            data-row-id={row.id}
            data-col-id={col.id}
            data-test={dataTest}
            style={{ height }}
            className={astraTwMerge(
                'group/cell relative h-full min-w-[28px] cursor-pointer self-stretch rounded-none border-b border-r bg-surface-base p-1 outline-none transition-all last:border-r-0',
                cellClassName?.(row, col),
                shadowClasses,
                isRowSelected && !isThisCellSelected ? '!bg-surface-strong' : '',
                !isThisCellSelected && !disableCellHover ? 'hover:bg-surface-muted' : '',
                isCursor ? [cursorStyles] : ''
            )}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
        >
            {children(row, col, isThisCellSelected)}
            {actionButton && (
                <div
                    className="absolute right-1 top-1 opacity-0 shadow transition-opacity focus-within:opacity-100 hover:opacity-100 focus:opacity-100 group-hover/cell:opacity-100"
                    onClick={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                >
                    {actionButton(row, col)}
                </div>
            )}
        </div>
    )
}

// Wrapper component that conditionally renders the appropriate cell type
export const TableCell = <TCol extends { id: Id }, TRow extends { id: Id }>(props: Props<TCol, TRow>) => {
    if (props.modalContentRender) {
        return <ModalTableCell {...props} modalContentRender={props.modalContentRender} />
    }
    return <BasicTableCell {...props} />
}
